import { Modal, Typography, Col, Row, Space, Dropdown, message } from "antd";
import { Guid } from "guid-typescript";
import { useState } from "react";
import { SearchQuery } from "../code/search-query/search-query";
import RewardsView from "../components/RewardsView";
import Loader from "../controls/Loader";
import {
  CompanyReward,
  DepartmentReward,
  Reward,
  UserReward,
} from "../repository/data.default";
import { getRewardsExport } from "../repository/data.repository";
import RewardFormDialog from "../components/RewardFormDialog";
import AssignRewardFormDialog from "../components/AssignRewardFormDialog";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

function RewardsPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [rewardInputMode, setRewardInputMode] = useState<
    "add" | "update" | "delete"
  >("add");
  const [rewardInputOpen, setRewardInputOpen] = useState(false);
  const [rewardInputValues, setRewardInputValues] = useState<Reward>({} as any);
  const [assignRewardInputOpen, setAssignRewardInputOpen] = useState(false);
  const [assignRewardInputValues, setAssignRewardInputValues] =
    useState<Reward>({} as any);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const permissions = useSelector(authorizeSelectors.permissions);

  // Events

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onRewardInputOpen(
    mode: "add" | "update" | "delete" | "assignReward",
    values?:
      | Reward
      | CompanyReward
      | DepartmentReward
      | UserReward,
  ): void {
    if (mode === "assignReward") {
      setAssignRewardInputOpen(true);
      setAssignRewardInputValues(values || ({} as any));
    } else {
      setRewardInputMode(mode);
      setRewardInputOpen(true);
      setRewardInputValues(values || ({} as any));
    }
  }

  function onRewardInputFinish(): void {
    setRewardInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onRewardInputClose(): void {
    setRewardInputOpen(false);
  }

  function onAssignRewardInputFinish(): void {
    setAssignRewardInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onAssignRewardInputClose(): void {
    setAssignRewardInputOpen(false);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading rewards export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getRewardsExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Rewards
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("Rewards", "create")}

            className={`${!permissions.can("Rewards", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "assignReward",
                  label: "Assign Reward",
                  disabled: !permissions.can("Rewards", "create"),
                  onClick: () => onRewardInputOpen("assignReward"),
                },
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("Rewards", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onRewardInputOpen("add")}
          >
            Add Reward
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <RewardsView
          trigger={triggerView}
          onAction={(key: string, record: any) =>
            onRewardInputOpen(key as any, record)
          }
          onSearch={onSearch}
        />
      </Col>
      <RewardFormDialog
        mode={rewardInputMode}
        open={rewardInputOpen}
        initialValues={rewardInputValues}
        onFinish={onRewardInputFinish}
        onCancel={onRewardInputClose}
      />
      <AssignRewardFormDialog
        open={assignRewardInputOpen}
        initialValues={assignRewardInputValues}
        onFinish={onAssignRewardInputFinish}
        onCancel={onAssignRewardInputClose}
      />
      {contextHolder}
    </Col>
  );
}

export default RewardsPage;
